import React, { useState, useRef } from 'react';
import MDialog from '../../../components/controls/MDialog';
import MAutoComplete from '../../../components/controls/MAutoComplete';
import { Stack, FormControlLabel, Button, LinearProgress, Checkbox, TextField, Box, Grid } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNotify, Confirm } from 'react-admin';
import PreviewIcon from '@mui/icons-material/Preview';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import getSQL from '../../../components/query-builder/common';
import { reloadPage } from '../../../utils/common';
import RuleCreator from './rule-creator';
import MNumber from '../../../components/controls/MNumber';
import CloneRuleDialog from './clone-rule-dialog';
import { EditRuleDialogState, RuleInfo, SelectOption } from '../types';
import { IData } from '../../../types';
import { MLabel } from '../../../components/controls/MLabel';

interface EditRuleProps {
    // The custom DataProvider instance exposes many dynamic OData-shaped methods; not worth modeling here.
    dataProvider: IData;
    closeDialog: () => void;
    refbtnFilter: React.MutableRefObject<any>;
    exchanges: SelectOption[];
    info: EditRuleDialogState;
    ruleInfo: RuleInfo | null;
}

const EditRule: React.FC<EditRuleProps> = ({ dataProvider, closeDialog, refbtnFilter, exchanges, info, ruleInfo }) => {
    const { open, record, isEdit, resource } = info;
    const { columnDefs, conditionFields, actionFields, initialState, dlgStyles } = ruleInfo || {};
    const [openConfirm, setOpenConfirm] = useState(false);
    const [openApplyConfirm, setOpenApplyConfirm] = useState(false);
    const [openCloneDialog, setOpenCloneDialog] = useState(false);
    // True while the user is editing a locally-drafted clone (not yet persisted).
    const [isDraftClone, setIsDraftClone] = useState(false);
    const [ruleName, setRuleName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [active, setActive] = useState(false);
    const [valid, setValid] = useState(true);
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [applyFor, setApplyFor] = useState<SelectOption[] | null>(null);
    const [priority, setPriority] = useState(1);
    const [disable, setDisable] = useState(false);//for display MBackDrop
    const notify = useNotify();
    const actionQuery = useRef<any>(null);
    const conditionQuery = useRef<any>(null);
    const apiRef = useGridApiRef();

    React.useEffect(() => {
        if (isEdit) {
            setRuleName(record.name ?? '');
            setActive(record.active ?? false);
            setPriority(record.priority ?? 1);
        } else {
            setRuleName('');
            setActive(true);
        }
        setPreviewData([]);
        setValid(true);
    }, [record]);

    const handleRuleNameChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setRuleName(ev.target.value);
    };
    const handleActive = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setActive(ev.target.checked);
    };
    const SaveRule = (_action?: any) => {
        let data = getData();
        if (!isValidData(data)) return;

        if (_action === 'delete') {
            data!.name = '';
            setOpenConfirm(false);
        }

        setDisable(true);
        dataProvider.postData('DynamicAdjustRule', 'Save', data)
            .then((response: any) => {
                setDisable(false);
                setApplyFor(null);
                if (response?.data) {//data = rule_name
                    // isDraftClone means we're saving a clone as a new entry even though isEdit is true.
                    if (isEdit && !isDraftClone)
                        notify('Update Success', { type: 'success', autoHideDuration: 5000 });
                    else
                        notify('Create Success', { type: 'success', autoHideDuration: 5000 });

                } else {
                    if (response.message)
                        notify(`Error: ${response?.message}`, { type: 'error', autoHideDuration: 5000 });
                    else if (response.data === '' && _action === 'delete') {
                        notify('Delete Success', { type: 'success', autoHideDuration: 5000 });
                    }
                    else
                        notify(`Error happened. Try to reload page. ${response}`, { type: 'error', autoHideDuration: 5000 });
                }
                handleClose();
                reloadPage(resource || 'DynamicAdjustRule', refbtnFilter);
            })
            .catch((err: any) => {
                notify(`Something went wrong. ${err}`, { type: 'error', autoHideDuration: 5000 });
            });
    };
    /**
     * Closes the edit dialog and resets any draft-clone state so the form is
     * clean if it is reopened for a different rule.
     */
    const handleClose = () => {
        setIsDraftClone(false);
        setRuleName(record.name ?? '');
        closeDialog();
    };

    /**
     * Opens the clone dialog so the user can supply a name for the duplicated rule.
     */
    const handleClone = () => {
        setOpenCloneDialog(true);
    };

    /**
     * Creates a local draft of the current rule with the supplied name.
     *
     * The Save API is intentionally NOT called at this point — a draft is
     * produced in-place so the user can inspect and further edit the cloned
     * configuration before committing it via the normal Save button.
     *
     * Steps:
     *  1. Close the clone dialog.
     *  2. Rename the form to the user-supplied value.
     *  3. Mark the form as a draft clone so getData() returns id='0', causing
     *     the next Save to create a brand-new rule rather than update the original.
     *
     * @param {string} newName - The name chosen by the user for the cloned rule.
     */
    const CloneRule = (newName: string) => {
        setOpenCloneDialog(false);
        setRuleName(newName);
        // Signal that this is an unsaved clone; getData() will clear the id.
        setIsDraftClone(true);
        notify(`Draft created from "${ruleName}". Edit as needed, then click Save.`, { type: 'info', autoHideDuration: 6000 });
    };

    const handleDelete = () => {
        setOpenConfirm(true);
    };

    const handleApply = () => {
        setOpenApplyConfirm(true);
    };

    const ApplyRule = () => {
        let data = getData();
        if (!isValidData(data)) return;

        setOpenApplyConfirm(false);
        setDisable(true);
        dataProvider.postData('DynamicAdjustRule', 'Apply', data)
            .then((response: any) => {
                setDisable(false);
                if (response?.data) {
                    notify(`Successfully applied rule to ${response.data.count} candidates`, { type: 'success', autoHideDuration: 5000 });
                } else {
                    notify(`Error: ${response?.message || 'Failed to apply rule'}`, { type: 'error', autoHideDuration: 5000 });
                }
            })
            .catch((err: any) => {
                setDisable(false);
                notify(`Something went wrong. ${err}`, { type: 'error', autoHideDuration: 5000 });
            });
    };

    const isValidData = (data: any) => {
        if (data === null) {
            notify('Please check your input!!!', { type: 'error', autoHideDuration: 3000 });
            return false;
        }
        setValid(true);
        if (data.condition === null) {
            notify('Require rule!!!', { type: 'error', autoHideDuration: 3000 });
            return false;
        }
        if (data.action === null || !data.action.includes('{')) {
            notify(data.action || 'Require action!!!', { type: 'error', autoHideDuration: 3000 });
            return false;
        }

        return true;
    };
    const showMatchCondition = () => {
        let data = getData();
        if (!isValidData(data)) return;

        setIsLoading(true);
        dataProvider.postData('DynamicAdjustRule', 'Preview', data)
            .then((response: any) => {
                if (response.data) {
                    let arr = displayData(response.data);
                    notify(`Found ${arr.length} candidates`, { type: 'success', autoHideDuration: 5000 });
                    // Check the columns is have value then show it
                    if (initialState?.hideColumns) {
                        let hideColumns: IData = {};
                        Object.keys(initialState.hideColumns).map((col: any) => {
                            hideColumns[col] = arr.some((e: any) => e[col]) || false;
                        });
                        apiRef.current.setColumnVisibilityModel(hideColumns);
                    }
                    setPreviewData(arr);
                }
                else {
                    notify(`Something went wrong. Status: ${response.status || response.message}`, { type: 'error', autoHideDuration: 5000 });
                }
                setIsLoading(false);
            })
            .catch((err: any) => {
                notify(`Error !!! . Status: ${err.status}`, { type: 'error', autoHideDuration: 5000 });
                setIsLoading(false);
            });

    };
    const displayData = (data: any) => {
        return data.map((x: any) => {
            x.name = x.rename_Event || x.name;
            x.venue = x.rename_Venue || x.venue;
            x.city = x.rename_City || x.city;
            x.state = x.rename_State || x.state;
            x.account_Tags = x.account_Tags?.replace(/^,|,$/g, ''); // ',Skip1,Skip2,' => 'Skip1,Skip2'
            return x;
        });
    };

    const getData = () => {
        if (!!!ruleName || ruleName === '') {
            setValid(false);
            return null;
        }
        let actionJson = actionQuery.current.getValues('json');
        if (actionJson) {
            let requireActionFieldIds = (actionFields ?? []).filter((x: any) => x.required).map((x: any) => x.id);
            if (requireActionFieldIds.length) {
                let tmp = JSON.parse(actionJson);
                let inputedValue = tmp.rules.filter((x: any) => requireActionFieldIds.some((y: any) => y === x.id)).map((x: any) => x.id);
                if (!requireActionFieldIds.every((element: any) => inputedValue.includes(element))) {
                    actionJson = `Require rules: ${requireActionFieldIds.join()}`;
                }
            }
        }
        let conditionJson = conditionQuery.current.getValues('json');
        return {
            // When saving a draft clone the id must be cleared so the backend creates a new entry.
            id: isDraftClone ? '0' : (record.id || '0'),
            categoryId: isEdit ? record.categoryId : Number((document.querySelector('[name="categoryId"]') as HTMLInputElement)?.value),
            applyfor: applyFor === null ? record.applyfor?.split(',').filter((e: any) => e !== '').join(',') : applyFor.map((x) => x.id).join(','),
            priority: priority,
            active: active,
            name: ruleName || '',
            action: actionJson,
            condition: conditionJson,
            sqlCondition: conditionJson ? getSQL(JSON.parse(conditionJson)) : conditionJson,
            createdby: record?.createdby || '',
            modified: record?.modified
        };
    };

    const displayExchange = () => {
        let exValues = record?.applyfor?.split(',').filter((e: any) => e !== '');
        if (!!!exValues)
            return exValues;
        return exchanges.filter((obj: any) => exValues.includes(obj.id));
    };

    return (
        <>
            <MDialog
                maxWidth={dlgStyles?.maxWidth || 'lg'}
                open={open}
                handleClose={handleClose}
                title={isEdit ? `#${record.id}` : 'New Rule'}
                name={'edit-rule-dialoge'}
                disabled={disable}
                action={
                    <Stack direction="row" justifyContent={'space-between'} width="100%">
                        <FormControlLabel label="Active" control={<Checkbox defaultChecked={!isEdit ? true : record.active} checked={active} onChange={handleActive} />} />
                        <Stack direction="row" spacing={1} justifyContent={'flex-end'}>
                            <Button startIcon={<SendIcon />} color='info' variant='contained' onClick={SaveRule}>Save</Button>
                            {record.categoryId === 1 && (
                                <Button startIcon={<PlayArrowIcon />} color='success' variant='contained' onClick={handleApply}>Apply</Button>
                            )}
                            {isEdit && (
                                <Button startIcon={<ContentCopyIcon />} color='secondary' variant='contained' onClick={handleClone}>Clone</Button>
                            )}
                            <Button startIcon={<DeleteIcon />} color={'error'} variant='contained' onClick={handleDelete}>Delete</Button>
                        </Stack>
                    </Stack>
                }
                children={
                    <div>
                        <MLabel key={'name'} name={'Rule Name'} />
                        <TextField
                            value={ruleName ?? ''}
                            fullWidth
                            required
                            error={!valid}
                            helperText={valid ? '' : 'Rule name can not be empty'}
                            label="Rule"
                            onChange={handleRuleNameChange}
                            placeholder="eg. Ban venue abc..." variant="outlined" />

                        <Grid container spacing={2} >
                            <Grid size={8}>
                                <MLabel key={'applyfor'} name={'Apply For'} />
                                <MAutoComplete
                                    name="applyfor"
                                    variant='outlined'
                                    value={(displayExchange || '') as any}
                                    choices={exchanges as any[]}
                                    onChange={(values) => setApplyFor(values as any)}
                                />
                            </Grid>
                            <Grid size={4}>
                                <MLabel key={'priority'} name={'Priority'} />
                                <MNumber
                                    variant="outlined"
                                    value={record?.priority ?? 1}
                                    onChange={(e: any) => setPriority(e)} />
                            </Grid>
                        </Grid>

                        <RuleCreator
                            isEdit={isEdit}
                            editData={record}
                            conditionQueryRef={conditionQuery}
                            actionQueryRef={actionQuery}
                            conditionFields={conditionFields}
                            actionFields={actionFields} />
                        <br />

                        <Button startIcon={<PreviewIcon />} color='info' variant='contained' onClick={showMatchCondition}>Preview</Button>

                        <MLabel key={'math'} name={'Match Candidates'} h={'h6'} />
                        {isLoading && <LinearProgress color="secondary" />}
                        <Box sx={{ height: previewData.length > 0 ? '60vh' : 100, width: '100%' }}>
                            <DataGrid
                                apiRef={apiRef}
                                rows={previewData}
                                columns={columnDefs as any}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 25,
                                        },
                                    },
                                    columns: {
                                        columnVisibilityModel: initialState?.hideColumns || {}
                                    },
                                    sorting: {
                                        sortModel: initialState?.sortModel
                                    }
                                }}
                            />
                        </Box>
                    </div>
                }
            />
            <Confirm isOpen={openConfirm} content={`Delete rule #${ruleName}. Are you sure?`} title={'Delete rule'} onConfirm={() => SaveRule('delete')} onClose={() => setOpenConfirm(false)} />
            <Confirm isOpen={openApplyConfirm} content={`Apply rule "${ruleName}" to all matching candidates? This will update the database records.`} title={'Apply rule'} onConfirm={ApplyRule} onClose={() => setOpenApplyConfirm(false)} />
            <CloneRuleDialog
                open={openCloneDialog}
                sourceName={ruleName}
                onClose={() => setOpenCloneDialog(false)}
                onConfirm={CloneRule}
            />
        </>
    );
};

export default React.memo(EditRule);